/** A memo of the mounted message window, never a second detached history.
 * Placeholders defer moving live nodes until reconciliation commits the new shell. */
export class MessageNodes {
  private owner = '';
  private mounted = new Map<string, { signature: string; node: HTMLElement }>();
  private next = new Map<string, string>();
  private retained = new WeakMap<Node, HTMLElement>();
  private built = new WeakSet<Node>();
  private kept = new WeakSet<Node>();
  private revisions = new WeakMap<Node, HTMLElement>();
  clear(): void {
    this.owner = ''; this.mounted.clear(); this.next.clear();
    this.retained = new WeakMap(); this.built = new WeakSet(); this.kept = new WeakSet(); this.revisions = new WeakMap();
  }
  begin(owner: string): void {
    if (this.owner !== owner) this.clear();
    this.owner = owner; this.next.clear();
    this.retained = new WeakMap(); this.built = new WeakSet(); this.kept = new WeakSet(); this.revisions = new WeakMap();
  }
  render(id: string, signature: string, build: () => HTMLElement): HTMLElement {
    this.next.set(id, signature);
    const previous = this.mounted.get(id);
    if (previous?.signature === signature && previous.node.isConnected) {
      const placeholder = document.createElement('article');
      placeholder.dataset.retainedMessage = id;
      this.retained.set(placeholder, previous.node); this.kept.add(previous.node);
      return placeholder;
    }
    const fresh = build(); this.built.add(fresh);
    if (previous?.node.isConnected && previous.node.dataset.mediaOwner
      && previous.node.dataset.mediaOwner === fresh.dataset.mediaOwner) this.revisions.set(fresh, previous.node);
    return fresh;
  }
  // Changed dependencies require fresh closures even when their HTML happens to match.
  isFresh(node: Node): boolean { return this.built.has(node); }
  isRetained(node: Node): boolean { return this.kept.has(node); }
  private resolveRevision(fresh: HTMLElement): HTMLElement {
    const old = this.revisions.get(fresh); if (!old?.isConnected) return fresh;
    this.revisions.delete(fresh);
    // Audio and video are direct message children with no render-owned closures. Keep both
    // media and article mounted; detaching either would exit native fullscreen.
    // Everything interactive is fresh so its callbacks see the current message.
    const media = [...old.children].filter((child): child is HTMLMediaElement => child instanceof HTMLMediaElement);
    const next = [...fresh.childNodes].map(child => child instanceof HTMLMediaElement
      ? media.find(player => player.cloneNode(false).isEqualNode(child.cloneNode(false))) ?? child : child);
    if (!next.some(child => media.includes(child as HTMLMediaElement))) return fresh;
    for (const attribute of [...old.attributes]) if (!fresh.hasAttribute(attribute.name)) old.removeAttribute(attribute.name);
    for (const attribute of [...fresh.attributes]) old.setAttribute(attribute.name, attribute.value);
    const retained = new Set(next.filter(child => media.includes(child as HTMLMediaElement)));
    for (const child of [...old.childNodes]) if (!retained.has(child)) child.remove();
    next.forEach((child,index) => { const at = old.childNodes[index] ?? null; if (at !== child) old.insertBefore(child, at); });
    return old;
  }
  resolve(node: Node): Node {
    if (node instanceof HTMLElement && this.revisions.has(node)) return this.resolveRevision(node);
    if (node instanceof Element) for (const fresh of [...node.querySelectorAll<HTMLElement>('.message')]) {
      if (this.revisions.has(fresh)) fresh.replaceWith(this.resolveRevision(fresh));
    }
    const retained = this.retained.get(node);
    if (retained) return retained;
    if (node instanceof Element) for (const placeholder of node.querySelectorAll('[data-retained-message]')) {
      const message = this.retained.get(placeholder);
      if (message) placeholder.replaceWith(message);
    }
    return node;
  }
  remember(root: HTMLElement): void {
    const mounted = new Map<string, { signature: string; node: HTMLElement }>();
    for (const node of root.querySelectorAll<HTMLElement>('#messages .message[data-message-id]')) {
      const id = node.dataset.messageId!; const signature = this.next.get(id);
      if (signature !== undefined) mounted.set(id, { signature, node });
    }
    this.mounted = mounted; this.next.clear();
  }
}
